"use client";

import type { ICalendarEvent } from "@cmp-src/calendar/CalendarDayView/CalendarDayView";
import { DayViewCalendar } from "@cmp-src/calendar/CalendarDayView/CalendarDayView";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@cmp-src/primitives/Popover";
import type { ISchedule, Office, ScheduleBuffer } from "@gettactic/api";
import { getScheduleBufferMinutes } from "@gettactic/helpers/src/schedules/scheduleBuffer";
import { CalendarRange } from "lucide-react";
import React from "react";

interface RoomDayViewPopoverProps {
  roomName: string;
  /** Everything already booked in the room on `day`. */
  schedules: ISchedule[];
  day: Date;
  /** The reservation the approval would book, drawn on top of the day. */
  pending: { title: string; start: Date; end: Date; buffer: ScheduleBuffer };
  office?: Office;
}

/** Wall clock, the way every calendar in the app reads reservation times. */
const toWallClockString = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}T${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes()
  ).padStart(2, "0")}:00`;

/**
 * The room's day, buffers included, without leaving the request. It answers the
 * one question the pickers raise — "does this fit?" — which a blocked-time line
 * alone cannot.
 */
export const RoomDayViewPopover: React.FC<RoomDayViewPopoverProps> = ({
  roomName,
  schedules,
  day,
  pending,
  office
}) => {
  const events: ICalendarEvent[] = React.useMemo(() => {
    const booked = schedules.map((schedule) => {
      const buffer = getScheduleBufferMinutes(schedule.buffer);
      return {
        id: schedule.id,
        title: schedule.title || "Reserved",
        startTime: schedule.start.replace("Z", ""),
        endTime: schedule.end.replace("Z", ""),
        location: "",
        color: "rgba(150,150,150,0.25)",
        category: "",
        bufferBeforeMinutes: buffer.before,
        bufferAfterMinutes: buffer.after
      };
    });

    return [
      ...booked,
      {
        id: "pending-approval",
        title: pending.title || "This request",
        startTime: toWallClockString(pending.start),
        endTime: toWallClockString(pending.end),
        location: "",
        color: "rgba(25,141,202,0.55)",
        category: "",
        bufferBeforeMinutes: Math.max(0, pending.buffer.before_minutes),
        bufferAfterMinutes: Math.max(0, pending.buffer.after_minutes)
      }
    ];
  }, [schedules, pending]);

  return (
    <Popover>
      <PopoverTrigger className="flex items-center gap-1.5 font-inter font-medium text-primary text-xs hover:underline">
        <CalendarRange aria-hidden="true" className="h-3.5 w-3.5" />
        Day view
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] p-0">
        <p className="border-neutral-100 border-b px-3 py-2 font-inter font-semibold text-neutral-900 text-sm">
          {roomName}
        </p>
        <div className="h-[22rem]">
          <DayViewCalendar events={events} currentDate={day} office={office} />
        </div>
      </PopoverContent>
    </Popover>
  );
};
